import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Spinner } from "@heroui/react";
import axiosInstance from "../../../shared/api/axiosInstance";
import { deleteCookie } from "../../../shared/utils/helpers";
import { trackException, trackInfo, LogLevel } from "../../../shared/services/appInsights";
import { AppInsightsActions, AppInsightsComponents, AppInsightsErrorTypes } from "../../../shared/services/appInsightsConstants";
import { TLoggedInUserInfo } from "../../../shared/types/loggedInUserInfo";

const Logout = () => {
  const [isLoggingOut, setIsLoggingOut] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const loggedInUserInfo = useOutletContext<TLoggedInUserInfo>();

  useEffect(() => {
    const performLogout = async () => {
      // Track logout attempt
      trackInfo("User logout initiated", { 
        userId: loggedInUserInfo?.id || "unknown",
        emailAddress: loggedInUserInfo?.emailAddress || "unknown",
        component: AppInsightsComponents.LOGOUT,
        action: AppInsightsActions.LOGOUT_ATTEMPT
      });

      try {
        await axiosInstance.post(
          "/token/logout",
          {},
          { withCredentials: true }
        );
        
        // Always delete the cookie regardless of API success
        deleteCookie("MSILoggedInEmailAddress");
        
        // Track successful logout
        trackInfo("User logout successful", { 
          userId: loggedInUserInfo?.id || "unknown",
          emailAddress: loggedInUserInfo?.emailAddress || "unknown",
          component: AppInsightsComponents.LOGOUT,
          action: AppInsightsActions.LOGOUT_SUCCESS
        });
        
        // Small delay for better UX
        setTimeout(() => {
          navigate("/account/login", { replace: true });
        }, 1000);
        
      } catch (error) {
        console.error("Logout API failed:", error);
        
        // Track logout failure (but still proceed with local logout)
        trackException(
          error as Error,
          LogLevel.Warning, // Warning since we still log out locally
          { 
            userId: loggedInUserInfo?.id || "unknown",
            emailAddress: loggedInUserInfo?.emailAddress || "unknown",
            component: AppInsightsComponents.LOGOUT,
            action: AppInsightsActions.LOGOUT_API_FAILURE,
            errorType: AppInsightsErrorTypes.API_ERROR
          }
        );
        
        // Even if API fails, still delete cookie and redirect
        deleteCookie("MSILoggedInEmailAddress");
        setError("Logout completed with warnings. You have been signed out.");
        
        setTimeout(() => {
          navigate("/account/login", { replace: true });
        }, 2000);
      } finally {
        setIsLoggingOut(false);
      }
    };

    performLogout();
  }, [navigate, loggedInUserInfo?.id, loggedInUserInfo?.emailAddress]);

  return (
    <div className="form col-span-6 md:col-span-3 lg:col-span-4 flex items-center justify-center min-h-screen py-8">
      <div className="flex flex-col items-center justify-center mx-auto space-y-4">
        {isLoggingOut ? (
          <>
            <Spinner
              size="lg"
              label="Logging out..."
              classNames={{ label: "text-md" }}
              variant="simple"
            />
            <p className="text-gray-600">Please wait while we sign you out securely.</p>
          </>
        ) : (
          <>
            {error ? (
              <>
                <div className="text-yellow-600 text-center">
                  <p className="font-semibold">⚠️ {error}</p>
                  <p className="text-sm mt-2">Redirecting to login...</p>
                </div>
              </>
            ) : (
              <>
                <div className="text-green-600 text-center">
                  <p className="font-semibold">✅ Successfully logged out!</p>
                  <p className="text-sm mt-2">Redirecting to login...</p>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Logout;
